/*
 * <vast-variant-dropdown>
 * Custom-Dropdown fuer die Variantenauswahl (ESN-Stil):
 * Bild + Name pro Eintrag, Haekchen bei Auswahl, ausgegraut bei
 * nicht verfuegbaren Werten. Auf Mobile (<750px) oeffnet die Liste
 * als Bottom-Sheet-Overlay von unten (Backdrop + Scroll-Lock).
 *
 * PORTAL: Beim Oeffnen auf Mobile werden Liste + Backdrop nach
 * document.body verschoben und beim Schliessen zurueckgeholt.
 * Grund: Dawns product__info-wrapper traegt scroll-trigger/
 * animate--slide-in (transform) - ein transformierter Vorfahre wird
 * zum Containing Block fuer position:fixed, das Sheet wuerde sonst
 * relativ zum Wrapper statt zum Viewport positioniert (falsche
 * Position, Luecken links/rechts, Backdrop unvollstaendig).
 *
 * Funktionsprinzip: Das native <select> bleibt als "Source of Truth"
 * im DOM (unsichtbar, sobald die Component initialisiert ist). Die
 * Bilder kommen aus dem [data-variant-images]-JSON; bevorzugt wird
 * die Variante, die neben dem Optionswert auch die uebrigen aktuell
 * gewaehlten Optionen trifft ([data-selected-variant]-JSON) - so
 * zeigt das Dropdown z.B. bei 2700g die 2700g-Bilder.
 *
 * Bei Auswahl wird select.value gesetzt und ein bubbelndes
 * change-Event dispatcht -> Dawns variant-selects, Preis-Update,
 * URL-Handling und variant-auto-correct laufen unveraendert weiter.
 *
 * Ohne JavaScript bleibt das native <select> sichtbar und
 * funktionsfaehig (Progressive Enhancement).
 */
if (!customElements.get('vast-variant-dropdown')) {
  customElements.define(
    'vast-variant-dropdown',
    class VastVariantDropdown extends HTMLElement {
      static BODY_LOCK_CLASS = 'vast-dd-scroll-lock';

      connectedCallback() {
        this.select = this.querySelector('select.select__select');
        if (!this.select) return;
        if (this.querySelector('.vast-dropdown__toggle')) return; // bereits initialisiert

        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onViewportChange = this.onViewportChange.bind(this);
        this.onWindowScroll = this.onWindowScroll.bind(this);

        this.optionIndex = parseInt(this.dataset.optionPosition || '1', 10) - 1;
        this.variantData = this.readVariantData();
        this.selectedOptions = this.readSelectedOptions();

        this.buildUI();
        this.addEventListener('keydown', this.onKeydown);
        this.classList.add('is-ready');
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        window.removeEventListener('resize', this.onViewportChange);
        window.removeEventListener('scroll', this.onViewportChange);
        window.removeEventListener('scroll', this.onWindowScroll);
        if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
        document.body.classList.remove(VastVariantDropdown.BODY_LOCK_CLASS);
        // Portalierte Elemente aufraeumen, falls die Component waehrend
        // eines offenen Sheets ersetzt wird (Section-Rerender)
        if (this.list?.parentElement === document.body) this.list.remove();
        if (this.backdrop?.parentElement === document.body) this.backdrop.remove();
      }

      /* ---------- Daten ---------- */

      readVariantData() {
        try {
          const json = this.closest('variant-selects')?.querySelector('[data-variant-images]')?.textContent;
          return json ? JSON.parse(json) : [];
        } catch (error) {
          console.warn('vast-variant-dropdown: Varianten-JSON nicht lesbar', error);
          return [];
        }
      }

      readSelectedOptions() {
        const picker = this.closest('variant-selects');
        const selected = [];

        /*
         * Auswahl aus dem DOM lesen (gecheckte Kacheln + Select-Werte).
         * Die Zuordnung Wert -> Optionsposition laeuft ueber variantData:
         * dort ist bekannt, an welcher Position jeder Wert vorkommt -
         * robuster als Namens-/ID-Parsing, das je nach Markup fehlschlaegt.
         * Das DOM hat Vorrang, weil data-selected-variant bei einer nicht
         * existierenden Kombination auf die erste verfuegbare Variante
         * (falsche Groesse) zurueckfallen kann.
         */
        const assign = (value) => {
          if (value == null || value === '') return;
          for (const variant of this.variantData) {
            const index = variant.options ? variant.options.indexOf(value) : -1;
            if (index > -1) {
              selected[index] = value;
              return;
            }
          }
        };

        if (picker) {
          picker.querySelectorAll('input[type="radio"]:checked').forEach((input) => assign(input.value));
          picker.querySelectorAll('select.select__select').forEach((select) => assign(select.value));
        }
        if (selected.some((value) => value != null)) return selected;

        // Fallback: JSON (wenn im DOM nichts zuzuordnen war)
        try {
          const json = picker?.querySelector('[data-selected-variant]')?.textContent;
          const variant = json ? JSON.parse(json) : null;
          return variant?.options || [];
        } catch (error) {
          return [];
        }
      }

      get textSoldout() {
        return this.dataset.textSoldout || 'Ausverkauft';
      }

      get textUnavailable() {
        return this.dataset.textUnavailable || 'Nicht verfügbar';
      }

      /*
       * Status eines Optionswerts in Kombination mit den uebrigen aktuell
       * gewaehlten Optionen (z.B. Groesse):
       * - 'unavailable': Kombination existiert nicht (z.B. Geschmack gibt es
       *   in dieser Groesse gar nicht)
       * - 'soldout': Kombination existiert, ist aber nicht lieferbar
       * - 'available': normal waehlbar
       */
      statusFor(option) {
        const value = option.value;

        // Fallback ohne Varianten-JSON: nur Dawns Sammelstatus verfuegbar
        if (!this.variantData.length) {
          return option.dataset.optionAvailable === 'false' ? 'soldout' : 'available';
        }

        const matches = this.variantData.filter((variant) => {
          if (variant.options?.[this.optionIndex] !== value) return false;
          return variant.options.every((optionValue, index) => {
            if (index === this.optionIndex) return true;
            return this.selectedOptions[index] == null || optionValue === this.selectedOptions[index];
          });
        });

        if (!matches.length) return 'unavailable';
        if (matches.some((variant) => variant.available)) return 'available';
        return 'soldout';
      }

      appendStatusChip(parent, status) {
        if (status === 'available') return;
        const chip = document.createElement('span');
        chip.className = 'vast-dropdown__status';
        chip.textContent = status === 'soldout' ? this.textSoldout : this.textUnavailable;
        parent.appendChild(chip);
      }

      /*
       * Badge eines Optionswerts (Varianten-Metafeld vast.flavor_badge):
       * bewusst ueber ALLE Groessen aggregiert - das Badge wird nur an
       * einer Variante pro Geschmack gepflegt (z.B. 900g) und gilt fuer
       * den Geschmack als Ganzes, unabhaengig von der gewaehlten Groesse.
       */
      badgeFor(value) {
        const match = this.variantData.find(
          (variant) => variant.options?.[this.optionIndex] === value && variant.badge
        );
        return match ? match.badge : null;
      }

      appendBadgeChip(parent, badge) {
        const chip = document.createElement('span');
        chip.className = 'vast-dropdown__badge';
        chip.textContent = badge;
        parent.appendChild(chip);
      }

      /*
       * Bild fuer einen Optionswert: unter allen Varianten mit diesem Wert
       * (an dieser Optionsposition) gewinnt die mit den meisten Treffern
       * gegen die uebrigen aktuell gewaehlten Optionen (z.B. Groesse).
       */
      imageFor(value) {
        let best = null;
        let bestScore = -1;

        for (const variant of this.variantData) {
          if (!variant.image || variant.options?.[this.optionIndex] !== value) continue;

          let score = 0;
          variant.options.forEach((optionValue, index) => {
            if (index === this.optionIndex) return;
            if (this.selectedOptions[index] != null && optionValue === this.selectedOptions[index]) score++;
          });

          if (score > bestScore) {
            best = variant;
            bestScore = score;
          }
        }

        return best ? best.image : null;
      }

      /* ---------- UI-Aufbau ---------- */

      buildUI() {
        const selected = this.select.selectedOptions[0] || this.select.options[0];

        // Toggle-Button (geschlossener Zustand)
        this.toggle = document.createElement('button');
        this.toggle.type = 'button';
        this.toggle.className = 'vast-dropdown__toggle';
        this.toggle.setAttribute('aria-haspopup', 'listbox');
        this.toggle.setAttribute('aria-expanded', 'false');
        const selectLabel = this.closest('.product-form__input')?.querySelector('.form__label');
        if (selectLabel) this.toggle.setAttribute('aria-label', selectLabel.textContent.trim());

        this.appendImage(this.toggle, selected);
        this.toggleLabel = document.createElement('span');
        this.toggleLabel.className = 'vast-dropdown__label';
        const toggleName = document.createElement('span');
        toggleName.className = 'vast-dropdown__name';
        toggleName.textContent = selected ? selected.value : '';
        this.toggleLabel.appendChild(toggleName);
        if (selected) {
          const toggleStatus = this.statusFor(selected);
          if (toggleStatus === 'unavailable') this.toggle.classList.add('is-unavailable');
          if (toggleStatus === 'soldout') this.toggle.classList.add('is-soldout');
          this.appendStatusChip(this.toggleLabel, toggleStatus);
          // Badge nur wenn kein Status-Chip - Verfuegbarkeit hat Vorrang
          if (toggleStatus === 'available') {
            const toggleBadge = this.badgeFor(selected.value);
            if (toggleBadge) this.appendBadgeChip(this.toggleLabel, toggleBadge);
          }
        }
        this.toggle.appendChild(this.toggleLabel);

        // Varianten-Zaehler "+N weitere" (nur im Toggle, generisch fuer
        // Geschmaecker/Farben/etc.; Text per data-text-more ueberschreibbar)
        const optionCount = this.select.options.length;
        if (optionCount > 1) {
          const count = document.createElement('span');
          count.className = 'vast-dropdown__count';
          count.textContent = `+${optionCount - 1} ${this.dataset.textMore || 'weitere'}`;
          this.toggle.appendChild(count);
        }

        this.toggle.insertAdjacentHTML('beforeend', this.chevronSvg());
        this.toggle.addEventListener('click', (event) => {
          event.preventDefault();
          // verhindert, dass derselbe Klick zusaetzlich den
          // Document-Outside-Click-Handler erreicht
          event.stopPropagation();
          this.toggleList();
        });

        // Backdrop (nur auf Mobile sichtbar, schliesst das Bottom-Sheet)
        this.backdrop = document.createElement('div');
        this.backdrop.className = 'vast-dropdown__backdrop';
        this.backdrop.addEventListener('click', () => this.toggleList(false));

        // Options-Liste (offener Zustand; Mobile: Bottom-Sheet)
        this.list = document.createElement('div');
        this.list.className = 'vast-dropdown__list';
        this.list.setAttribute('role', 'listbox');
        this.list.addEventListener('keydown', this.onKeydown);

        Array.from(this.select.options).forEach((option) => {
          const item = document.createElement('button');
          item.type = 'button';
          item.className = 'vast-dropdown__item';
          item.setAttribute('role', 'option');
          item.dataset.value = option.value;
          item.setAttribute('aria-selected', option.selected ? 'true' : 'false');
          if (option.selected) item.classList.add('is-selected');

          const status = this.statusFor(option);
          if (status === 'unavailable') item.classList.add('is-unavailable');
          if (status === 'soldout') item.classList.add('is-soldout');

          this.appendImage(item, option);

          const label = document.createElement('span');
          label.className = 'vast-dropdown__item-label';
          const name = document.createElement('span');
          name.className = 'vast-dropdown__name';
          name.textContent = option.value;
          label.appendChild(name);
          this.appendStatusChip(label, status);
          // Badge nur wenn kein Status-Chip - Verfuegbarkeit hat Vorrang
          if (status === 'available') {
            const badge = this.badgeFor(option.value);
            if (badge) this.appendBadgeChip(label, badge);
          }
          item.appendChild(label);

          if (option.selected) item.insertAdjacentHTML('beforeend', this.checkSvg());

          // 'unavailable' (Kombination existiert nicht): nicht klickbar -
          // eine Auswahl haette keine sinnvolle Funktion.
          // 'soldout' bleibt waehlbar (zeigt den Ausverkauft-Zustand).
          if (status === 'unavailable') {
            item.disabled = true;
            item.setAttribute('aria-disabled', 'true');
          } else {
            item.addEventListener('click', () => this.choose(option.value));
          }
          this.list.appendChild(item);
        });

        this.appendChild(this.toggle);
        this.appendChild(this.backdrop);
        this.appendChild(this.list);
      }

      appendImage(parent, option) {
        if (!option) return;
        const src = this.imageFor(option.value);
        if (!src) return;
        const wrap = document.createElement('span');
        wrap.className = 'vast-dropdown__image';
        const img = document.createElement('img');
        img.src = src;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 120;
        img.height = 120;
        wrap.appendChild(img);
        parent.appendChild(wrap);
      }

      chevronSvg() {
        return `<svg class="vast-dropdown__chevron" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path fill="currentColor" d="M3.793 8.293a1 1 0 0 1 1.414 0L12 15.086l6.793-6.793a1 1 0 1 1 1.414 1.414l-7.5 7.5a1 1 0 0 1-1.414 0l-7.5-7.5a1 1 0 0 1 0-1.414Z" fill-rule="evenodd" clip-rule="evenodd"></path></svg>`;
      }

      checkSvg() {
        return `<svg class="vast-dropdown__check" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path fill="currentColor" d="M9.55 17.05 4.5 12l1.414-1.414 3.636 3.636 8.536-8.536L19.5 7.1l-9.95 9.95Z"/></svg>`;
      }

      /* ---------- Interaktion ---------- */

      get isMobileSheet() {
        return window.matchMedia('(max-width: 749px)').matches;
      }

      toggleList(force) {
        const isOpen = this.classList.contains('is-open');
        const open = force !== undefined ? force : !isOpen;

        // Schutz gegen "oeffnet und schliesst sofort" (Touch-Geraete
        // feuern fuer einen Tap gelegentlich doppelte Click-Events,
        // u.a. verstaerkt durch Tracking-Scripte): Schliessversuche
        // innerhalb von 350ms nach dem Oeffnen ignorieren.
        if (!open && isOpen && Date.now() - (this.openedAt || 0) < 350) return;

        if (open) {
          this.openedAt = Date.now();

          // PORTAL (Desktop + Mobile): Liste + Backdrop nach document.body.
          // Grund: sticky/transformierte Wrapper (product__column-sticky,
          // scroll-trigger) bilden eigene Stacking-Contexts - innerhalb
          // davon kann die Liste von nachfolgenden Inhalten (Accordions)
          // ueberlagert werden und untere Eintraege sind nicht klickbar.
          document.body.appendChild(this.backdrop);
          document.body.appendChild(this.list);

          if (this.isMobileSheet) {
            this.clearDesktopPosition();
            document.body.classList.add(VastVariantDropdown.BODY_LOCK_CLASS);
          } else {
            this.applyDesktopPosition();
          }
        }

        this.classList.toggle('is-open', open);
        this.list.classList.toggle('is-open', open);
        this.backdrop.classList.toggle('is-open', open);
        this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');

        if (open) {
          this.list.querySelector('.is-selected')?.scrollIntoView({ block: 'nearest' });
          // Verzoegert registrieren: der oeffnende Klick und das
          // initiale scrollIntoView duerfen nicht sofort schliessen
          setTimeout(() => {
            document.addEventListener('click', this.onDocumentClick);
            window.addEventListener('resize', this.onViewportChange);
            // Desktop: beim Seiten-Scroll Position nachfuehren (Toggle
            // sitzt im sticky Container und bewegt sich relativ zum
            // Dokument) - NICHT schliessen
            if (!this.isMobileSheet) window.addEventListener('scroll', this.onWindowScroll, { passive: true });
          }, 100);
        } else {
          document.removeEventListener('click', this.onDocumentClick);
          window.removeEventListener('resize', this.onViewportChange);
          window.removeEventListener('scroll', this.onWindowScroll);
          if (this.scrollRaf) {
            cancelAnimationFrame(this.scrollRaf);
            this.scrollRaf = null;
          }
          document.body.classList.remove(VastVariantDropdown.BODY_LOCK_CLASS);
          this.clearDesktopPosition();

          // Portal rueckgaengig: Elemente zurueck in die Component
          if (this.backdrop.parentElement === document.body) this.appendChild(this.backdrop);
          if (this.list.parentElement === document.body) this.appendChild(this.list);
        }
      }

      /* Desktop: Panel dokument-absolut exakt unter dem Toggle positionieren */
      applyDesktopPosition() {
        const rect = this.toggle.getBoundingClientRect();
        this.list.style.position = 'absolute';
        this.list.style.top = `${rect.bottom + window.scrollY + 6}px`;
        this.list.style.left = `${rect.left + window.scrollX}px`;
        this.list.style.width = `${rect.width}px`;
        this.list.style.right = 'auto';
        this.list.style.bottom = 'auto';
      }

      clearDesktopPosition() {
        this.list.style.position = '';
        this.list.style.top = '';
        this.list.style.left = '';
        this.list.style.width = '';
        this.list.style.right = '';
        this.list.style.bottom = '';
      }

      /* Bei Resize schliessen (Viewport kann zwischen Desktop/Mobile wechseln) */
      onViewportChange() {
        this.toggleList(false);
      }

      /* Desktop: beim Seiten-Scroll die Panel-Position nachfuehren
         (rAF-gedrosselt) - der Toggle sitzt im sticky Container und
         bewegt sich beim Scrollen relativ zum Dokument */
      onWindowScroll() {
        if (this.scrollRaf) return;
        this.scrollRaf = requestAnimationFrame(() => {
          this.scrollRaf = null;
          if (this.classList.contains('is-open') && !this.isMobileSheet) {
            this.applyDesktopPosition();
          }
        });
      }

      onDocumentClick(event) {
        // Liste kann portaliert (ausserhalb der Component) sein
        if (!this.contains(event.target) && !this.list.contains(event.target)) {
          this.toggleList(false);
        }
      }

      choose(value) {
        this.toggleList(false);
        this.toggle.focus();
        if (this.select.value === value) return;

        this.select.value = value;
        // Bubbelndes change-Event -> variant-selects uebernimmt (Preis, URL, Auto-Korrektur).
        // vastUserInitiated markiert das Event als echte Nutzeraktion (isTrusted = false
        // bei programmatischen Events) fuer nachgelagerte Logik.
        const changeEvent = new Event('change', { bubbles: true });
        changeEvent.vastUserInitiated = true;
        this.select.dispatchEvent(changeEvent);
      }

      onKeydown(event) {
        const items = Array.from(this.list.querySelectorAll('.vast-dropdown__item:not([disabled])'));
        const isOpen = this.classList.contains('is-open');

        switch (event.key) {
          case 'Escape':
            if (isOpen) {
              event.preventDefault();
              this.toggleList(false);
              this.toggle.focus();
            }
            break;
          case 'ArrowDown':
          case 'ArrowUp': {
            event.preventDefault();
            if (!isOpen) {
              this.toggleList(true);
              (this.list.querySelector('.is-selected') || items[0])?.focus();
              return;
            }
            const currentIndex = items.indexOf(document.activeElement);
            const delta = event.key === 'ArrowDown' ? 1 : -1;
            const nextIndex = Math.min(Math.max(currentIndex + delta, 0), items.length - 1);
            items[nextIndex]?.focus();
            break;
          }
          case 'Home':
            if (isOpen) {
              event.preventDefault();
              items[0]?.focus();
            }
            break;
          case 'End':
            if (isOpen) {
              event.preventDefault();
              items[items.length - 1]?.focus();
            }
            break;
        }
      }
    }
  );
}